/** @file cameraControl: the viewport's canvas layout (integer zoom), look-at point and store camera sync. */
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { screenDeltaToWorld } from '@zilo/pose3d-render-core/camera/screenDeltaToWorld';
import type { CanvasFit } from '@zilo/pose3d-runtime-babylon/canvas/canvasFit';
import { fitCanvas } from '@zilo/pose3d-runtime-babylon/canvas/fitCanvas';
import { watchDevicePixelRatio } from '@zilo/pose3d-runtime-babylon/canvas/watchDevicePixelRatio';
import type { CameraState } from '../../state/editorTypes';
import type { ViewportScene } from './createViewportScene';
import type { CameraControl } from './viewportCtx';
import { viewportLayout, type ViewportLayout } from './viewportLayout';
import { fitZoomFor } from './fitZoomFor';
import { zoomScale } from './zoomScale';

/** What the default view frames: the look-at point (the puppet's bounding centre) and the puppet's rest height. */
export type CameraFrame = { home: Vec3; height: number };

/**
 * The control plus the per-frame store sync, `reframe` (a puppet opened: frame it and return to the fitted zoom) and
 * the teardown of its resize watchers.
 */
export type CameraControlHandle = CameraControl & { sync(camera: CameraState): void; reframe(frame: CameraFrame): void; dispose(): void };

/**
 * The canvas covers the container at full resolution: its CSS size is exactly the backing store / dpr (a fractional
 * dpr would otherwise resample the whole image by a sub-pixel and soften it), and any residual scale is nearest.
 */
function fill(canvas: HTMLCanvasElement, device: [number, number], dpr: number): void {
  Object.assign(canvas.style, { position: 'absolute', left: '0px', top: '0px', width: `${String(device[0] / dpr)}px`, height: `${String(device[1] / dpr)}px`, imageRendering: 'pixelated' });
}

/**
 * Fit: target height = container height x dpr / scale, `scale` an integer upscale (default `fitZoomFor`: the puppet's
 * height in about 65 % of the view; wheel +-1 within 1..8; profiles with `snap: none` always draw at 1), refit on
 * container resize and dpr change. `home` (and `0`, reset camera) returns to the fitted zoom and the frame's centre.
 * The canvas fills the container at CSS x dpr (`viewportLayout`); the puppet target sits centred on it.
 * `pan` moves the look-at point in the view plane by target pixels; `home` looks at `home`. `sync` applies the
 * store's yaw and pitch when they changed.
 */
export function cameraControl(vs: ViewportScene, canvas: HTMLCanvasElement, container: HTMLElement, profile: Profile, frame: CameraFrame): CameraControlHandle {
  let scale: number | null = null;
  let framed = frame;
  const home = (): Vec3 => framed.home;
  const scaleNow = (h: number, dpr: number): number => scale ?? fitZoomFor(framed.height, h, profile.pixelsPerUnit, dpr);
  let fitted: CanvasFit | null = null;
  let laid: ViewportLayout | null = null;
  let target: Vec3 = [frame.home[0], frame.home[1], frame.home[2]];
  let applied = { yaw: NaN, pitch: NaN };
  const refit = (): void => {
    const dpr = window.devicePixelRatio || 1;
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w <= 0 || h <= 0) return;
    fitted = fitCanvas(w, h, dpr, Math.max(2, Math.floor((h * dpr) / scaleNow(h, dpr))), profile.snap);
    laid = viewportLayout(w, h, fitted);
    fill(canvas, laid.device, dpr);
    vs.engine.setSize(laid.device[0], laid.device[1]);
    vs.camera.setViewport(fitted.width, fitted.height);
    vs.setLayout(laid);
  };
  const lookAt = (p: Vec3): void => { target = [p[0], p[1], p[2]]; vs.camera.lookAt(target); };
  const unwatch = watchDevicePixelRatio(refit);
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refit);
  observer?.observe(container);
  refit();
  lookAt(target);
  return {
    zoom: (step) => {
      const dpr = window.devicePixelRatio || 1;
      scale = zoomScale(scaleNow(container.clientHeight, dpr), step, profile.pixelsPerUnit, dpr);
      refit();
    },
    pan: (dx, dy) => {
      const d = screenDeltaToWorld(-dx, -dy, vs.camera.view());
      lookAt([target[0] + d[0], target[1] + d[1], target[2] + d[2]]);
    },
    lookAt, home: () => { scale = null; refit(); lookAt(home()); }, fit: () => fitted, layout: () => laid,
    reframe: (f) => { framed = f; scale = null; refit(); lookAt(home()); },
    sync: (camera) => {
      if (camera.yawDeg !== applied.yaw) vs.camera.setYaw(camera.yawDeg);
      if (camera.pitchDeg !== applied.pitch) vs.camera.setPitch(camera.pitchDeg);
      applied = { yaw: camera.yawDeg, pitch: camera.pitchDeg };
    },
    dispose: () => { unwatch(); observer?.disconnect(); },
  };
}
